import ContentBlock from '../../ContentBlock';
import { NarrativeDisclaimer } from '../../NarrativeDisclaimer';
import { NarrativeSponsoredNotice } from '../../NarrativeSponsoredNotice';
import loadable from '@loadable/component';
import type {
  Narrative,
  NarrativeUpdate,
} from '@/pages/Narrative/hooks/useNarrative';
import { memo } from 'react';
import { NarrativeViewTabs } from './NarrativeViewTabs';
import { NarrativeUpdates } from '../../NarrativeUpdates';
import { NarrativeUpdateCarousel } from '../../NarrativeUpdateCarousel';

const Rating = loadable(() => import('../../Rating'));
const NarrativeComment = loadable(() => import('../../NarrativeComment'), {
  resolveComponent: (c) => c.NarrativeComment,
});
const NarrativesRelated = loadable(() => import('../../NarrativesRelated'), {
  resolveComponent: (c) => c.NarrativesRelated,
});

type Props = {
  narrative: Narrative;
  publishedUpdates: NarrativeUpdate[];
  commentRef: React.RefObject<HTMLDivElement>;
};

export const NarrativeMainContentBase = ({
  narrative,
  publishedUpdates,
  commentRef,
}: Props) => {
  const {
    id: narrativeId,
    company,
    slug,
    contentBlocks,
    userHoldsPosition,
    commentKey,
    owner,
    isSponsored,
    latestPublishedUpdate,
    commentCount,
  } = narrative;

  if (!company) throw new Error("Narrative's company is missing");

  const latestUpdate = publishedUpdates[0];
  const contentHtml =
    latestPublishedUpdate?.coreHtml ?? contentBlocks[0]?.contentHtml ?? '';
  return (
    <NarrativeViewTabs updateCount={publishedUpdates.length}>
      {(tab) => {
        if (tab === 'updates')
          return (
            <article className="grid auto-rows-max gap-x2">
              <NarrativeUpdates updates={publishedUpdates} />
            </article>
          );

        return (
          <article className="grid auto-rows-max gap-x2">
            {latestUpdate && (
              <NarrativeUpdateCarousel updates={publishedUpdates} />
            )}

            <ContentBlock contentHtml={contentHtml} />

            {company && (
              <Rating
                companyId={company.id}
                narrativeId={narrativeId}
                uniqueSymbol={company.uniqueSymbol}
              />
            )}
            <NarrativeComment
              commentKey={commentKey}
              commentCount={commentCount}
              ref={commentRef}
            />
            <NarrativeDisclaimer
              userHoldsPosition={userHoldsPosition}
              authorDisplayName={owner?.displayName}
              companyTicker={company.uniqueSymbol}
              disclaimerType={owner?.disclaimerType}
            />
            <NarrativeSponsoredNotice
              companyName={company.name}
              isSponsored={isSponsored}
            />
            <NarrativesRelated slug={slug} />
          </article>
        );
      }}
    </NarrativeViewTabs>
  );
};

export const NarrativeMainContent = memo(NarrativeMainContentBase);
